import React, { useEffect, useRef, useState } from "react";
import { Loader2, Palette, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { ciApi } from "./ciApi";

const DEFAULT_PRIMARY = "#0d8f85";
const DEFAULT_SECONDARY = "#a87b1f";
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

const ColorField = ({ label, value, onChange, testId }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-white/45 mb-2">{label}</div>
    <div className="flex items-center gap-3">
      <input
        type="color"
        value={HEX_RE.test(value) ? value : "#000000"}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`${testId}-picker`}
        className="h-10 w-14 rounded-md border border-white/15 bg-transparent cursor-pointer"
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`${testId}-hex`}
        className="flex-1 rounded-md border border-white/15 bg-white/[0.04] px-3 py-2 text-sm text-white/85 font-mono focus:outline-none focus:border-teal-400/60"
      />
    </div>
  </div>
);

export const BrandingDialog = ({ assessmentId }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoUri, setLogoUri] = useState(null);
  const [primary, setPrimary] = useState(DEFAULT_PRIMARY);
  const [secondary, setSecondary] = useState(DEFAULT_SECONDARY);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    ciApi
      .get(`/assessments/${assessmentId}/branding`)
      .then((r) => {
        setLogoUri(r.data.logo_data_uri);
        setPrimary(r.data.accent_primary || DEFAULT_PRIMARY);
        setSecondary(r.data.accent_secondary || DEFAULT_SECONDARY);
      })
      .catch(() => toast.error("Failed to load branding"))
      .finally(() => setLoading(false));
  }, [open, assessmentId]);

  const uploadLogo = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      await ciApi.post(`/assessments/${assessmentId}/branding/logo`, form);
      const r = await ciApi.get(`/assessments/${assessmentId}/branding`);
      setLogoUri(r.data.logo_data_uri);
      toast.success("Client logo uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Logo upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    try {
      await ciApi.delete(`/assessments/${assessmentId}/branding/logo`);
      setLogoUri(null);
      toast.success("Client logo removed");
    } catch {
      toast.error("Failed to remove the logo");
    }
  };

  const save = async () => {
    if (!HEX_RE.test(primary) || !HEX_RE.test(secondary)) {
      toast.error("Colours must be 6-digit hex values like #0D8F85");
      return;
    }
    setSaving(true);
    try {
      await ciApi.put(`/assessments/${assessmentId}/branding`, {
        accent_primary: primary,
        accent_secondary: secondary,
      });
      toast.success("Branding saved — the PDF and PPTX exports will now use it");
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save branding");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="ci-branding-btn"
          className="inline-flex items-center gap-2 rounded-full border border-white/20 text-white/75 hover:bg-white/[0.06] font-bold text-sm px-5 py-2.5 transition-colors"
        >
          <Palette className="w-4 h-4" /> Branding
        </button>
      </DialogTrigger>
      <DialogContent data-testid="ci-branding-dialog" className="bg-[#0d1526] border-white/10 text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-white">Client branding for board packs</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-white/40" />
          </div>
        ) : (
          <div className="space-y-6">
            <p className="text-[12px] text-white/50 -mt-2">
              The client logo and accent colours are applied to both the PDF board pack and the PPTX board deck for this assessment.
            </p>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-white/45 mb-2">Client logo · PNG or JPEG, max 2 MB</div>
              <div className="flex items-center gap-4">
                <div className="h-16 w-36 rounded-md border border-dashed border-white/20 bg-white flex items-center justify-center overflow-hidden shrink-0">
                  {logoUri ? (
                    <img src={logoUri} alt="Client logo" data-testid="ci-branding-logo-preview" className="max-h-full max-w-full object-contain p-1" />
                  ) : (
                    <span className="text-[10px] text-black/40">No logo</span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    data-testid="ci-branding-upload-btn"
                    className="inline-flex items-center gap-2 rounded-full border border-teal-400/50 text-teal-300 hover:bg-teal-400/10 disabled:opacity-40 font-bold text-[12px] px-4 py-1.5 transition-colors"
                  >
                    {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    {logoUri ? "Replace" : "Upload"}
                  </button>
                  {logoUri && (
                    <button
                      onClick={removeLogo}
                      data-testid="ci-branding-remove-logo-btn"
                      className="inline-flex items-center gap-2 rounded-full border border-red-400/40 text-red-300 hover:bg-red-400/10 font-bold text-[12px] px-4 py-1.5 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Remove
                    </button>
                  )}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  className="hidden"
                  data-testid="ci-branding-logo-input"
                  onChange={(e) => uploadLogo(e.target.files?.[0])}
                />
              </div>
            </div>
            <ColorField label="Primary accent · replaces teal" value={primary} onChange={setPrimary} testId="ci-branding-primary" />
            <ColorField label="Secondary accent · replaces gold" value={secondary} onChange={setSecondary} testId="ci-branding-secondary" />
            <div className="flex items-center justify-between pt-1">
              <button
                onClick={() => {
                  setPrimary(DEFAULT_PRIMARY);
                  setSecondary(DEFAULT_SECONDARY);
                }}
                data-testid="ci-branding-reset-btn"
                className="text-[12px] text-white/50 hover:text-white/80 underline underline-offset-2"
              >
                Reset to SAMANVAI defaults
              </button>
              <button
                onClick={save}
                disabled={saving}
                data-testid="ci-branding-save-btn"
                className="inline-flex items-center gap-2 rounded-full bg-teal-500 hover:bg-teal-400 disabled:opacity-40 text-[#04211c] font-bold text-sm px-6 py-2.5 transition-colors"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save branding
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
